import {
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { TreeProps } from 'antd';
import { Button, Dropdown, Tooltip, Tree, theme } from 'antd';
import type { CSSProperties, KeyboardEvent, MouseEvent } from 'react';
import { useState } from 'react';
import './index.less';

const useCollectionColors = (): CSSProperties => {
  const { token } = theme.useToken();
  return {
    '--tc-text': token.colorText,
    '--tc-muted': token.colorTextSecondary,
    '--tc-primary': token.colorPrimary,
    '--tc-primary-bg': token.colorPrimaryBg,
    '--tc-hover': token.colorFillTertiary,
    '--tc-count-bg': token.colorFillSecondary,
    '--tc-focus': token.colorPrimaryBorder,
    '--tc-border': token.colorBorderSecondary,
  } as CSSProperties;
};

const stopMenuActivation = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.stopPropagation();
  }
};

export const CollectionTree = ({ treeData, selectedKeys }: TreeProps) => {
  const colors = useCollectionColors();
  return (
    <div
      className="talent-collection-tree"
      style={colors}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Tree
        blockNode
        showIcon={false}
        showLine={{ showLeafIcon: false }}
        treeData={treeData}
        selectedKeys={selectedKeys}
      />
    </div>
  );
};

export const CollectionSectionHeader = ({
  title,
  onCreate,
}: {
  title: string;
  onCreate: () => void;
}) => {
  const colors = useCollectionColors();
  return (
    <div
      className="talent-collection-section"
      style={colors}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <h2 className="talent-collection-section-title">{title}</h2>
      <Tooltip title="新建收藏夹" placement="right">
        <Button
          className="talent-collection-create"
          type="text"
          size="small"
          icon={<PlusOutlined />}
          aria-label={`创建${title}`}
          onClick={onCreate}
          onKeyDown={stopMenuActivation}
        >
          <span className="talent-collection-create-label">新建</span>
        </Button>
      </Tooltip>
    </div>
  );
};

type CollectionMenuItemProps = {
  collection: { name: string };
  count?: number;
  countUnit: string;
  href: string;
  selected: boolean;
  depth: number;
  onNavigate: () => void;
  onEdit: () => void;
  onCreateChild: () => void;
  onDelete: () => void;
};

export const CollectionMenuItem = ({
  collection,
  count,
  countUnit,
  href,
  selected,
  depth,
  onNavigate,
  onEdit,
  onCreateChild,
  onDelete,
}: CollectionMenuItemProps) => {
  const colors = useCollectionColors();
  const [actionsOpen, setActionsOpen] = useState(false);

  const handleNavigate = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate();
  };

  return (
    <div
      className={`talent-collection-row${selected ? ' is-active' : ''}`}
      data-actions-open={actionsOpen || undefined}
      style={{ ...colors, '--tc-indent': `${depth * 16}px` } as CSSProperties}
    >
      <a
        className="talent-collection-link"
        href={href}
        title={collection.name}
        aria-current={selected ? 'page' : undefined}
        onClick={handleNavigate}
        onKeyDown={stopMenuActivation}
      >
        <span className="talent-collection-name">{collection.name}</span>
        {count != null && (
          <span
            className="talent-collection-count"
            title={`${count} ${countUnit}`}
          >
            {count}
          </span>
        )}
      </a>
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        onOpenChange={setActionsOpen}
        menu={{
          items: [
            { key: 'edit', label: '编辑', icon: <EditOutlined /> },
            { key: 'add-child', label: '新建子收藏夹', icon: <PlusOutlined /> },
            { type: 'divider' },
            {
              key: 'delete',
              label: '删除',
              icon: <DeleteOutlined />,
              danger: true,
            },
          ],
          onClick: ({ key, domEvent }) => {
            domEvent.preventDefault();
            domEvent.stopPropagation();
            if (key === 'edit') onEdit();
            if (key === 'add-child') onCreateChild();
            if (key === 'delete') onDelete();
          },
        }}
      >
        <Button
          className="talent-collection-actions"
          type="text"
          size="small"
          icon={<MoreOutlined />}
          aria-label={`收藏夹“${collection.name}”的操作`}
          aria-haspopup="menu"
          aria-expanded={actionsOpen}
          onKeyDown={stopMenuActivation}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        />
      </Dropdown>
    </div>
  );
};
